import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { motion } from 'framer-motion'
import {
  ChartBarIcon,
  ClockIcon,
  TrophyIcon,
  FireIcon,
  BookOpenIcon,
  AcademicCapIcon,
  StarIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PlayIcon,
  PauseIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { RootState } from '../store'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import { formatDuration, formatNumber, formatDate } from '../utils/helpers'

const Dashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const { currentSession, isActive, timeRemaining: pomodoroTimeRemaining } = useSelector((state: RootState) => state.pomodoro)
  const { currentExam, timeRemaining, answers, currentQuestion, results, stats: examStats } = useSelector((state: RootState) => state.exam)
  const { stats: studyStats, sessions } = useSelector((state: RootState) => state.study)
  const { badges, achievements, xp, level, coins, streak } = useSelector((state: RootState) => state.gamification)
  const { decks, stats: flashcardStats } = useSelector((state: RootState) => state.flashcard)

  // Calculate real stats from Redux data
  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]
    const todaySessions = sessions.filter(session => session.date === today)
    const todayStudyTime = todaySessions.reduce((total, session) => total + session.duration, 0)
    
    return {
      todayStudyTime,
      weeklyStudyTime: studyStats?.weeklyStudyTime || 0,
      totalXP: xp || 0,
      currentStreak: streak?.current || 0,
      completedExams: results?.length || 0,
      averageScore: examStats?.averageScore || 0,
      totalBadges: badges?.filter(badge => badge.unlockedAt).length || 0,
      rank: 15 // This would come from a leaderboard service
    }
  }, [sessions, studyStats, xp, streak, results, examStats, badges])

  // Generate recent activities from real data
  const recentActivities = useMemo(() => {
    const activities = []
    
    // Add recent exam results
    const recentExams = results?.slice(-3).map(result => ({
      id: `exam-${result.id}`,
      type: 'exam',
      title: result.examTitle,
      score: Math.round(result.percentage),
      date: new Date(result.date),
      icon: AcademicCapIcon,
      color: result.percentage >= 80 ? 'text-blue-600' : result.percentage >= 60 ? 'text-yellow-600' : 'text-red-600'
    })) || []
    
    // Add recent study sessions
    const recentSessions = sessions?.slice(-3).map(session => ({
      id: `session-${session.id}`,
      type: 'study',
      title: `${session.subject} - ${session.topic}`,
      duration: session.duration,
      date: new Date(session.date),
      icon: BookOpenIcon,
      color: 'text-green-600'
    })) || []
    
    // Add recent badges
    const recentBadges = badges?.filter(badge => badge.unlockedAt)
      .sort((a, b) => new Date(b.unlockedAt!).getTime() - new Date(a.unlockedAt!).getTime())
      .slice(0, 2)
      .map(badge => ({
        id: `badge-${badge.id}`,
        type: 'badge',
        title: `${badge.name} Rozeti Kazanıldı`,
        date: new Date(badge.unlockedAt!),
        icon: TrophyIcon,
        color: 'text-yellow-600'
      })) || []
    
    // Combine and sort by date
    activities.push(...recentExams, ...recentSessions, ...recentBadges)
    return activities.sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 5)
  }, [results, sessions, badges])

  // Generate upcoming tasks from achievements and study plans
  const upcomingTasks = useMemo(() => {
    const tasks = []
    
    // Add incomplete achievements as tasks
    const incompleteAchievements = achievements?.filter(achievement => !achievement.completed)
      .slice(0, 3)
      .map((achievement, index) => ({
        id: `achievement-${achievement.id}`,
        title: achievement.title,
        type: 'achievement',
        date: new Date(Date.now() + (index + 1) * 24 * 60 * 60 * 1000),
        priority: achievement.current / achievement.target > 0.7 ? 'high' : achievement.current / achievement.target > 0.4 ? 'medium' : 'low'
      })) || []
    
    // Add flashcard review tasks
    const reviewDecks = decks?.filter(deck => {
      // Check if deck needs review (simplified logic)
      return deck.cards && deck.cards.length > 0
    }).slice(0, 2).map((deck, index) => ({
      id: `review-${deck.id}`,
      title: `${deck.name} Kartları Tekrarı`,
      type: 'flashcard',
      date: new Date(Date.now() + (index + 1) * 2 * 60 * 60 * 1000),
      priority: 'medium'
    })) || []
    
    // Add study reminders based on subjects with low progress
    const lowProgressSubjects = Object.entries(studyStats?.subjectProgress || {})
      .filter(([_, progress]) => progress < 70)
      .slice(0, 2)
      .map(([subject, _], index) => ({
        id: `study-${subject}`,
        title: `${subject} Çalışması`,
        type: 'study',
        date: new Date(Date.now() + (index + 1) * 4 * 60 * 60 * 1000),
        priority: 'high'
      }))
    
    tasks.push(...incompleteAchievements, ...reviewDecks, ...lowProgressSubjects)
    return tasks.slice(0, 5)
  }, [achievements, decks, studyStats])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Welcome Section */}
      <motion.div variants={itemVariants}>
        <Card variant="gradient" className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10" />
          <div className="relative z-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Hoş geldin, {user?.name}! 👋
                </h1>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  Bugün harika bir çalışma günü geçirmeye hazır mısın?
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-2">
                    <StarIcon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-white">
                    Seviye {level || user?.level || 1}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Quick Stats */}
      <motion.div variants={itemVariants}>
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          <Card variant="glass" hover>
            <div className="flex flex-col sm:flex-row items-center sm:items-start">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-2 sm:mb-0">
                <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="sm:ml-4 text-center sm:text-left">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Bugün</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {formatDuration(stats.todayStudyTime * 60)}
                </p>
              </div>
            </div>
          </Card>

          <Card variant="glass" hover>
            <div className="flex flex-col sm:flex-row items-center sm:items-start">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-2 sm:mb-0">
                <FireIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="sm:ml-4 text-center sm:text-left">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Seri</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {stats.currentStreak} gün
                </p>
              </div>
            </div>
          </Card>

          <Card variant="glass" hover>
            <div className="flex flex-col sm:flex-row items-center sm:items-start">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-2 sm:mb-0">
                <TrophyIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="sm:ml-4 text-center sm:text-left">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">XP</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(stats.totalXP)}
                </p>
              </div>
            </div>
          </Card>

          <Card variant="glass" hover>
            <div className="flex flex-col sm:flex-row items-center sm:items-start">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center mb-2 sm:mb-0">
                <ChartBarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="sm:ml-4 text-center sm:text-left">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Ortalama</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {stats.averageScore}%
                </p>
              </div>
            </div>
          </Card>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Current Activity */}
        <motion.div variants={itemVariants} className="lg:col-span-2 order-1 lg:order-1">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Şu Anki Aktivite
              </h2>
            </div>

            {currentSession ? (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <ClockIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                        Pomodoro Oturumu
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        {currentSession.subject}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant={isActive ? 'warning' : 'success'}
                    size="sm"
                    icon={isActive ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
                    className="w-full sm:w-auto"
                  >
                    {isActive ? 'Duraklat' : 'Devam Et'}
                  </Button>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {formatDuration(pomodoroTimeRemaining)}
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-1000"
                      style={{ 
                        width: `${currentSession ? ((currentSession.duration - pomodoroTimeRemaining) / currentSession.duration) * 100 : 0}%` 
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : currentExam ? (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-xl p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center">
                      <AcademicCapIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                        Sınav Devam Ediyor
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        {currentExam.title}
                      </p>
                    </div>
                  </div>
                  <Button variant="primary" size="sm" className="w-full sm:w-auto">
                    Sınava Dön
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                      {currentQuestion + 1}/{currentExam.questions.length}
                    </p>
                    <p className="text-xs sm:text-xs text-gray-600 dark:text-gray-400">Soru</p>
                  </div>
                  <div>
                    <p className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                      {formatDuration(timeRemaining)}
                    </p>
                    <p className="text-xs sm:text-xs text-gray-600 dark:text-gray-400">Kalan Süre</p>
                  </div>
                  <div>
                    <p className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                      {Object.values(answers).filter(a => a !== null).length}
                    </p>
                    <p className="text-xs sm:text-xs text-gray-600 dark:text-gray-400">Cevaplanan</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PlayIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                </div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Aktif Çalışma Yok
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6">
                  Yeni bir çalışma oturumu başlatmaya hazır mısın?
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <Button variant="primary" gradient className="w-full sm:w-auto">
                    Pomodoro Başlat
                  </Button>
                  <Button variant="outline" className="w-full sm:w-auto">
                    Sınav Çöz
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Upcoming Tasks */}
        <motion.div variants={itemVariants} className="order-2 lg:order-2">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Yaklaşan Görevler
              </h2>
              <CalendarIcon className="w-5 h-5 text-gray-400" />
            </div>

            <div className="space-y-4">
              {upcomingTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`
                      w-2 h-2 rounded-full
                      ${task.priority === 'high' ? 'bg-red-500' : 
                        task.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'}
                    `} />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {task.title}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {formatDate(task.date, 'HH:mm')}
                      </p>
                    </div>
                  </div>
                  <Button size="xs" variant="ghost">
                    Başla
                  </Button>
                </div>
              ))}
            </div>

            <Button variant="outline" fullWidth className="mt-4">
              Tümünü Görüntüle
            </Button>
          </Card>
        </motion.div>
      </div>

      {/* Progress Section */}
      <motion.div variants={itemVariants}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Subject Progress */}
          <Card>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                Konu İlerlemeleri
              </h2>
              <Button variant="ghost" size="sm" className="w-full sm:w-auto">
                Detaylar
              </Button>
            </div>

            <div className="space-y-4">
              {Object.entries(studyStats?.subjectProgress || {
                'Matematik': 75,
                'Türkçe': 60, 
                'Fen Bilimleri': 85,
                'İngilizce': 45,
                'Sosyal Bilgiler': 70
              }).map(([subject, progress], index) => {
                const colors = ['blue', 'green', 'purple', 'yellow', 'red']
                const color = colors[index % colors.length]
                const total = Math.floor(100 + Math.random() * 50) // Simulated total topics
                const completed = Math.floor((progress / 100) * total)
                const item = { subject, progress, color, total, completed }
                return (
                <div key={item.subject} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {item.subject}
                    </span>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {item.completed}/{item.total} konu
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className={`bg-gradient-to-r from-${item.color}-400 to-${item.color}-600 h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${item.progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                    <span>%{item.progress} tamamlandı</span>
                    <span className={`text-${item.color}-600 dark:text-${item.color}-400 font-medium`}>
                      {item.progress >= 80 ? 'Mükemmel' : 
                       item.progress >= 60 ? 'İyi' : 
                       item.progress >= 40 ? 'Orta' : 'Geliştirilmeli'}
                    </span>
                  </div>
                </div>
                )
              })}
            </div>
          </Card>

          {/* Weekly Progress */}
          <Card>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                Haftalık İlerleme
              </h2>
              <Button variant="ghost" size="sm" className="w-full sm:w-auto">
                Rapor
              </Button>
            </div>

            <div className="space-y-6">
              {/* Study Time Progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Çalışma Süresi
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDuration(stats.weeklyStudyTime * 60)} / {formatDuration(25 * 60 * 60)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-blue-400 to-blue-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((stats.weeklyStudyTime / (25 * 60)) * 100, 100)}%` }}
                  />
                </div>
              </div>

              {/* Exam Progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Sınav Hedefi
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {stats.completedExams} / 15 sınav
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-green-400 to-green-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${(stats.completedExams / 15) * 100}%` }}
                  />
                </div>
              </div>

              {/* XP Progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    XP Hedefi
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {formatNumber(stats.totalXP)} / 5000 XP
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-purple-400 to-purple-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${(stats.totalXP / 5000) * 100}%` }}
                  />
                </div>
              </div>

              {/* Achievement Summary */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                    <TrophyIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Toplam {stats.totalBadges} rozet kazandın!
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Seviye {level || 1} • {formatNumber(stats.totalXP)} XP
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </motion.div>

      {/* Recent Activities */}
      <motion.div variants={itemVariants}>
        <Card>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              Son Aktiviteler
            </h2>
            <Button variant="ghost" size="sm" className="w-full sm:w-auto">
              Tümünü Gör
            </Button>
          </div>

          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div
                key={activity.id}
                className="flex items-start sm:items-center space-x-3 sm:space-x-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0`}>
                  <activity.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${activity.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                    {activity.title}
                  </p>
                  <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <span>{formatDate(activity.date, 'HH:mm')}</span>
                    {activity.score && (
                      <>
                        <span className="hidden sm:inline">•</span>
                        <span className="flex items-center">
                          {activity.score >= 80 ? (
                            <ArrowTrendingUpIcon className="w-3 h-3 text-green-500 mr-1" />
                          ) : (
                            <ArrowTrendingDownIcon className="w-3 h-3 text-red-500 mr-1" />
                          )}
                          {activity.score}%
                        </span>
                      </>
                    )}
                    {activity.duration && (
                      <>
                        <span className="hidden sm:inline">•</span>
                        <span>{activity.duration} dakika</span>
                      </>
                    )}
                  </div>
                </div>
                <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
              </div>
            ))}
          </div>
        </Card>
      </motion.div>
    </motion.div>
  )
}

export default Dashboard